import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, Target, Users, Zap, ExternalLink } from 'lucide-react';
import { PRODUCTS } from '../data/productData';

const ProductDetails = () => {
    const { slug } = useParams();
    const product = PRODUCTS.find(p => p.slug === slug);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (!product) {
        return (
            <div className="pt-32 pb-20 text-center">
                <h1 className="text-3xl font-bold mb-4">Product Not Found</h1>
                <Link to="/" className="text-primary hover:underline">Back to Home</Link>
            </div>
        );
    }

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1, delayChildren: 0.2 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6 }
        }
    };

    return (
        <div className="pt-24 min-h-screen bg-white">
            {/* Header / Hero */}
            <section className={`py-20 bg-gradient-to-br ${product.color} relative overflow-hidden`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <Link to="/#products" className="inline-flex items-center gap-2 text-primary font-bold mb-8 hover:gap-3 transition-all">
                        <ArrowLeft size={20} /> Back to Products
                    </Link>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="space-y-6"
                        >
                            <div className="text-6xl mb-4">{product.icon}</div>
                            <h1 className="text-4xl md:text-6xl font-bold text-dark">{product.name}</h1>
                            <p className="text-xl font-bold text-primary">{product.tagline}</p>
                            <p className="text-lg text-gray-600 leading-relaxed">{product.description}</p>

                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-primary text-white px-8 py-3 rounded-xl font-bold shadow-lg hover:shadow-primary/30 transition-all flex items-center gap-2"
                                onClick={() => window.location.href = `/contact?subject=Inquiry about ${product.name}`}
                            >
                                Get Started <ExternalLink size={20} />
                            </motion.button>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="rounded-2xl overflow-hidden shadow-2xl border border-white/20"
                        >
                            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">Key <span className="text-primary">Features</span></h2>
                        <div className="h-1 w-20 bg-primary mx-auto rounded-full" />
                    </div>

                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
                    >
                        {product.features.map((feature, idx) => {
                            const Icon = feature.icon;
                            return (
                                <motion.div
                                    key={idx}
                                    variants={itemVariants}
                                    className="p-8 rounded-2xl bg-light border border-gray-100 hover:shadow-soft-lg transition-all"
                                >
                                    <div className="p-4 bg-white rounded-xl w-fit mb-6 shadow-soft text-primary">
                                        <Icon size={28} />
                                    </div>
                                    <h3 className="text-xl font-bold text-dark mb-3">{feature.title}</h3>
                                    <p className="text-gray-500 text-sm leading-relaxed">{feature.desc}</p>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                </div>
            </section>

            {/* Benefits & Audience Section */}
            <section className="py-20 bg-dark text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
                        {/* Benefits */}
                        <div>
                            <h2 className="text-3xl font-bold mb-10 flex items-center gap-3">
                                <Zap className="text-primary" /> Key Benefits
                            </h2>
                            <div className="space-y-6">
                                {product.benefits.map((benefit, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1 }}
                                        className="flex items-start gap-4"
                                    >
                                        <CheckCircle2 size={24} className="text-primary shrink-0" />
                                        <p className="text-lg text-gray-300">{benefit}</p>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Audience */}
                        <div>
                            <h2 className="text-3xl font-bold mb-10 flex items-center gap-3">
                                <Target className="text-secondary" /> Target Audience
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {product.targetAudience.map((audience, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1 }}
                                        className="p-4 rounded-xl bg-white/5 border border-white/10 flex items-center gap-3"
                                    >
                                        <Users size={20} className="text-secondary" />
                                        <span className="font-bold">{audience}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Screenshots Gallery */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 text-center">Platform <span className="text-primary">Interface</span></h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {product.screenshots.map((img, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className="rounded-2xl overflow-hidden shadow-soft-lg border border-gray-100"
                            >
                                <img src={img} alt={`Screenshot ${idx + 1}`} className="w-full h-full object-cover" />
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-20 bg-light">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-5xl font-bold text-dark mb-8">Ready to transform your business with {product.name}?</h2>
                    <div className="flex flex-col sm:flex-row gap-6 justify-center">
                        <button
                            className="bg-primary text-white px-10 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-primary/30 transition-all"
                            onClick={() => window.location.href = `/contact?subject=Sales Inquiry for ${product.name}`}
                        >
                            Contact Sales
                        </button>
                        <button
                            className="bg-white border-2 border-primary text-primary px-10 py-4 rounded-xl font-bold text-lg hover:bg-primary hover:text-white transition-all"
                            onClick={() => window.location.href = `/contact?subject=Request Demo for ${product.name}`}
                        >
                            Request Demo
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ProductDetails;
